<template>
    <div class="pwa-payments-header mb-4">
        <div class="payments-title mb-3">
            <h2 class="mb-1">
                PWA Payments
            </h2>
            <div class="text-muted">
                Централізоване управління способами оплати для PWA-додатків.
            </div>
        </div>

        <div class="payments-card">
            <div class="country-tabs">
                <button
                    type="button"
                    class="country-tab"
                    :class="{ active: selectedCountry === country.code }"
                    v-for="country in usedCountries"
                    :key="country.code"
                    @click="selectCountry(country.code)"
                >
                    <span v-if="country.flag" class="mr-2">
                        {{ country.flag }}
                    </span>
                    {{ country.name }}
                </button>
            </div>

            <div class="gateway-tabs">
                <button
                    type="button"
                    class="gateway-card"
                    :class="{ active: selectedGateId === gate.id }"
                    v-for="gate in filteredGates"
                    :key="gate.id"
                    @click="selectGate(gate)"
                >
                    <span class="gateway-logo" :class="gate.view_class">
                        {{ gateShortName(gate) }}
                    </span>

                    <span class="gateway-title">
                        {{ gate.name }}
                    </span>
                </button>
            </div>

            <div class="header-actions mt-3" v-if="selectedGateId > 0">
                <div class="selected-gate-info">
                    <span class="text-muted">
                        {{ __t('SELECTED_GATE') }}:
                    </span>

                    <strong>
                        {{ selectedGate ? selectedGate.name : '' }}
                    </strong>
                </div>

                <b-button
                    variant="primary"
                    class="ml-auto"
                    @click="createPayment"
                >
                    <i class="fal fa-plus mr-1"></i>
                    Налаштувати шлюз
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "pwa_payments_header",

    props: {
        value: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            countries: [],
            gates: [],

            selectedCountry: '',
            selectedGateId: 0,

            localFilter: {
                search: '',
                country_code: '',
                gate_type: '',
                pay_gate_id: 0,
                status: ''
            }
        }
    },

    created() {
        this.localFilter = {
            ...this.localFilter,
            ...this.value
        };

        this.selectedCountry = this.localFilter.country_code || '';
        this.selectedGateId = parseInt(this.localFilter.pay_gate_id || 0);

        this.loadCountries();
        this.loadPwaGates();
    },

    methods: {
        async loadCountries() {
            try {
                const res = await axios.get('/api/v2/countries');

                if (res.data && Array.isArray(res.data.data)) {
                    this.countries = res.data.data.map((country) => {
                        return {
                            code: country.code,
                            name: country.name || country.title || country.code.toUpperCase(),
                            flag: country.flag || ''
                        };
                    });
                }
            } catch (e) {
                console.error('Countries load error', e);
            }
        },

        async loadPwaGates() {
            this.gates = [];

            await api.get('/internal/v1/payment/gates/pwa')
                .then(({ data }) => {
                    if (data.status === 'operation_success') {
                        this.gates = this.normalizeGates(data.data);

                        if (!this.selectedCountry && this.usedCountries.length > 0) {
                            this.selectCountry(this.usedCountries[0].code, false);
                        }
                    }
                })
                .catch(({ response }) => {
                    EventBus.$emit('show_toast', {
                        err: response?.data?.message || 'Payment gates load error',
                        type: "danger",
                        title: response?.data?.status || 'PWA Payments'
                    });
                });
        },

        normalizeGates(items) {
            if (!Array.isArray(items)) {
                return [];
            }

            return items
                .map((item) => {
                    let defaultParams = {};

                    if (typeof item.default_params === 'string' && item.default_params.length > 0) {
                        try {
                            defaultParams = JSON.parse(item.default_params);
                        } catch (e) {
                            defaultParams = {};
                        }
                    } else if (item.default_params && typeof item.default_params === 'object') {
                        defaultParams = item.default_params;
                    }

                    return {
                        id: parseInt(item.id),
                        type: item.type || '',
                        name: item.name || item.type || '',
                        country_code: item.country_code || '',
                        in_pwa: Boolean(item.in_pwa),
                        is_enable: Boolean(item.is_enable),
                        view_class: item.view_class || '',
                        default_params: defaultParams,
                        has_split: Object.prototype.hasOwnProperty.call(defaultParams, 'split')
                    };
                })
                .filter((item) => {
                    return item.id > 0 &&
                        item.type.length > 0 &&
                        item.in_pwa === true &&
                        item.is_enable === true;
                });
        },

        selectCountry(code, needEmit = true) {
            this.selectedCountry = code;
            this.localFilter.country_code = code;

            this.selectedGateId = 0;
            this.localFilter.pay_gate_id = 0;
            this.localFilter.gate_type = '';

            if (needEmit) {
                this.$emit('country-change', code);
                this.emitFilter();
            }
        },

        selectGate(gate) {
            if (!gate || !gate.id) {
                this.selectedGateId = 0;
                this.localFilter.pay_gate_id = 0;
                this.localFilter.gate_type = '';
                this.emitFilter();
                return;
            }

            this.selectedGateId = parseInt(gate.id);
            this.localFilter.pay_gate_id = parseInt(gate.id);
            this.localFilter.gate_type = gate.type;

            this.$emit('gate-change', {
                id: gate.id,
                type: gate.type,
                gate: gate
            });

            this.emitFilter();
        },

        createPayment() {
            if (!this.selectedGate || this.selectedGateId <= 0) {
                return;
            }

            this.$emit('create', {
                pay_gate_id: this.selectedGateId,
                gate_type: this.selectedGate.type,
                country_code: this.selectedCountry,
                gate: this.selectedGate
            });
        },

        emitFilter() {
            const filter = {
                ...this.localFilter,
                country_code: this.selectedCountry,
                pay_gate_id: this.selectedGateId,
                gate_type: this.selectedGate ? this.selectedGate.type : ''
            };

            this.$emit('input', filter);
            this.$emit('filter', filter);
        },

        gateShortName(gate) {
            const map = {
                liqpay: 'liq',
                wayforpay: 'wfp',
                mono: 'mono',
                privat_pay: '24',
                bog: 'bog',
                tpc: 'tbc',
                imoje: 'imoje',
                elavon: 'ela',
                sibs: 'sibs'
            };

            return map[gate.type] || gate.type.substring(0, 4);
        }
    },

    computed: {
        selectedGate() {
            if (this.selectedGateId <= 0) {
                return null;
            }

            return this.gates.find((gate) => {
                return parseInt(gate.id) === this.selectedGateId;
            }) || null;
        },

        usedCountries() {
            const gateCountryCodes = this.gates
                .map((gate) => gate.country_code)
                .filter((code) => !!code);

            const uniqueCodes = [...new Set(gateCountryCodes)];

            return uniqueCodes.map((code) => {
                const found = this.countries.find((country) => country.code === code);

                return {
                    code: code,
                    name: found ? found.name : code.toUpperCase(),
                    flag: found ? found.flag : ''
                };
            });
        },

        filteredGates() {
            if (!this.selectedCountry) {
                return this.gates;
            }

            return this.gates.filter((gate) => {
                return gate.country_code === this.selectedCountry;
            });
        }
    },

    watch: {
        value: {
            deep: true,
            handler(value) {
                this.localFilter = {
                    ...this.localFilter,
                    ...value
                };

                this.selectedCountry = this.localFilter.country_code || '';
                this.selectedGateId = parseInt(this.localFilter.pay_gate_id || 0);
            }
        }
    }
}
</script>

<style scoped>
.pwa-payments-header {
    width: 100%;
}

.payments-title h2 {
    font-weight: 600;
    color: #222;
}

.payments-card {
    background: #fff;
    border-radius: 6px;
    border: 1px solid #e5e5e5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .03);
    padding: 0;
}

.country-tabs {
    display: flex;
    align-items: center;
    gap: 22px;
    padding: 14px 18px 0 18px;
    border-bottom: 1px solid #eee;
}

.country-tab {
    border: 0;
    background: transparent;
    padding: 12px 12px 14px 12px;
    font-weight: 600;
    color: #444;
    border-bottom: 2px solid transparent;
    cursor: pointer;
}

.country-tab.active {
    color: #6f42c1;
    border-bottom-color: #886ab5;
}

.gateway-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    padding: 22px 18px;
}

.gateway-card {
    min-width: 180px;
    height: 54px;
    border: 1px solid #ddd;
    background: #fff;
    border-radius: 6px;
    display: flex;
    align-items: center;
    padding: 0 16px;
    cursor: pointer;
    transition: all .15s ease;
}

.gateway-card:hover {
    border-color: #886ab5;
}

.gateway-card.active {
    border-color: #886ab5;
    box-shadow: 0 0 0 1px #886ab5 inset;
    background: #fbf8ff;
}

.gateway-logo {
    width: 32px;
    height: 24px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    background: #886ab5;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    text-transform: uppercase;
}

.gateway-logo.btn-success {
    background: #1dc9b7;
}

.gateway-logo.btn-info {
    background: #2196f3;
}

.gateway-logo.btn-dark {
    background: #222;
}

.gateway-title {
    font-weight: 600;
    color: #333;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 18px 18px 18px;
}

.selected-gate-info {
    background: #f8f8fb;
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 8px 12px;
}

@media (max-width: 992px) {
    .gateway-card {
        min-width: 140px;
    }

    .header-actions {
        flex-wrap: wrap;
    }

    .header-actions .btn {
        margin-left: 0 !important;
    }
}
</style>
